<script setup lang="ts">
import { computed } from "vue";
import LabelText from "../LabelText.vue";
import ErrorMessage from "../ErrorMessage.vue";

// Props definition
interface Props {
  parentClass?: string;
  labelClass?: string;
  noteClass?: string;
  label?: string;
  labelText?: string;
  modelValue: boolean;
  asterisk?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  disabled?: boolean;
  name?: string;
  value?: string | number | boolean;
  indeterminate?: boolean;
  required?: boolean;
  note?: string;
  error?: boolean;
  errorMessage?: string | string[];
  errorType?: 'error' | 'warning' | 'info';
}

const props = withDefaults(defineProps<Props>(), {
  parentClass: '',
  labelClass: '',
  noteClass: '',
  label: '',
  labelText: '',
  asterisk: false,
  size: 'md',
  disabled: false,
  name: '',
  value: '',
  indeterminate: false,
  required: false,
  note: '',
  error: false,
  errorMessage: '',
  errorType: 'error'
});

// Emit events
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'change': [event: Event, value: boolean];
}>();

// Generate a unique ID for the checkbox
const uniqueId = computed(() => 
  `checkbox-${Math.random().toString(36).substring(2, 9)}`
);

// Compute checked state based on modelValue
const isChecked = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    emit('update:modelValue', value);
  }
});

// Check if there's an error message
const hasError = computed(() => {
  return !!props.errorMessage && 
    (typeof props.errorMessage === 'string' ? props.errorMessage.length > 0 : props.errorMessage.length > 0)
});

// Handle change event safely
const handleChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const newValue = target.checked;
  
  isChecked.value = newValue;
  emit('change', event, newValue);
};

// Handle keydown events for accessibility
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    isChecked.value = !isChecked.value;
  }
};

// Define size classes for checkbox
const checkboxSizeClasses: Record<'xs' | 'sm' | 'md' | 'lg', string> = {
  xs: 'h-4 w-4',
  sm: 'h-5 w-5',
  md: 'h-6 w-6',
  lg: 'h-7 w-7'
};

const sizeClasses: Record<'xs' | 'sm' | 'md' | 'lg', string> = {
  xs: 'py-1 px-2 text-xs h-9',
  sm: 'py-2 px-2 text-sm h-10',
  md: 'py-2.5 px-2 text-base h-11',
  lg: 'py-4 px-2 text-lg h-14'
};

// Computed classes for checkbox container
const checkboxContainerClasses = computed(() => {
  const base = [
    'flex items-center justify-center rounded border transition-all duration-200',
    checkboxSizeClasses[props.size],
    props.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
  ];

  // Border and background colors based on state and error
  if (props.error || hasError.value) {
    base.push('border-red-500');
    if (isChecked.value) {
      base.push('bg-red-500 border-red-500');
    }
  } else if (isChecked.value) {
    base.push('border-brand-500 bg-brand-500 dark:bg-brand-500');
  } else {
    base.push('border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800');
  }

  // Hover states (only when not disabled)
  if (!props.disabled) {
    if (props.error || hasError.value) {
      base.push('hover:border-red-600');
    } else if (!isChecked.value) {
      base.push('hover:border-brand-400 dark:hover:border-brand-400');
    }
  }

  return base;
});

// Computed classes for checkmark
const checkmarkClasses = computed(() => {
  const base = [
    'transition-all duration-200 transform',
    isChecked.value ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
  ];

  if (props.error || hasError.value) {
    base.push('text-white');
  } else {
    base.push('text-white dark:text-gray-900');
  }

  return base;
});

// Computed classes for indeterminate state
const indeterminateClasses = computed(() => {
  const base = [
    'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
    'h-0.5 w-3/4 rounded bg-current transition-all duration-200',
    props.indeterminate && !isChecked.value ? 'opacity-100' : 'opacity-0'
  ];

  if (props.error || hasError.value) {
    base.push('bg-white');
  } else {
    base.push('text-white dark:text-gray-900');
  }

  return base;
});
</script>

<template>
  <div :class="[parentClass, 'checkbox-container flex flex-col gap-1']">
    <div class="flex flex-col gap-2">
      <LabelText 
        v-if="labelText" 
        :labelText="labelText" 
        :inputId="uniqueId" 
        :asterisk="asterisk"
        :labelClass="labelClass"
      />
      <label 
        :for="uniqueId" 
        :class="[
          'inline-flex items-center cursor-pointer select-none transition-colors duration-200',
          props.disabled ? 'cursor-not-allowed opacity-60' : ''
        ]"
      >
        <div class="relative flex-shrink-0">
          <input
            type="checkbox"
            :id="uniqueId"
            :name="name"
            :value="value"
            :checked="isChecked"
            :disabled="disabled"
            :required="required"
            :indeterminate="indeterminate"
            class="sr-only"
            @change="handleChange"
            @keydown="handleKeydown"
            :aria-invalid="hasError"
            :aria-describedby="hasError ? `${uniqueId}-error` : undefined"
          />
          <div :class="checkboxContainerClasses">
            <!-- Checkmark -->
            <span :class="checkmarkClasses">
              <svg
                width="11"
                height="8"
                viewBox="0 0 11 8"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
                  stroke="currentColor"
                  stroke-width="0.4"
                />
              </svg>
            </span>
            
            <!-- Indeterminate state (minus sign) -->
            <span :class="indeterminateClasses" />
          </div>
        </div>
        <span 
          v-if="label"
          :class="[sizeClasses[props.size]]"
        >
          {{ label }}
        </span>
      </label>


    </div>

    <p
      v-if="note"
      v-html="note"
      :class="['text-xs font-normal', noteClass]"
    ></p>

    <ErrorMessage
      v-if="hasError"
      :message="errorMessage"
      :type="errorType"
      :input-id="uniqueId"
    />
  </div>
</template>

<style scoped>
/* Ensure proper focus styles for accessibility */
.sr-only:focus + div {
  /* @apply ring-2 ring-offset-2 ring-brand-500 dark:ring-brand-400; */
}

/* Smooth transitions */
.checkbox-container * {
  @apply transition-all duration-200;
}
</style>